// ── 견적서 등록/수정 모달 ──────────────────────────────────────────────────────
// 모바일: 바텀시트(드래그-투-디스미스) / 데스크탑: 센터 모달
// QuoteManagement.jsx에서 분리 (300줄 초과 방지)

import React from 'react';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { useOverlayClickClose } from '../../../hooks/useOverlayClickClose';
import { FreeItemsEditor } from './QuoteFormComponents';
import { SHEET_DISMISS_THRESHOLD } from './useQuoteManagement';
import {
  PRODUCT_OPTIONS, fmtComma, fmtBizNum,
  inputStyle, selectStyle, labelStyle, btnPrimary, btnSecondary,
} from './constants';
import {
  stdModalHeaderBar, stdModalHeaderIconBox, stdModalHeaderTitle, stdModalCloseBtn,
} from '../adminlayout/designStandard';

export default function QuoteFormModal({
  isMobile, showForm, setShowForm,
  sheetDragY, sheetDragging, sheetHandleRef,
  form, setField, editId,
  salesPersons, freeRows, setFreeRows, promotionalItems,
  saving, handleSave, formErr,
}) {
  useBodyScrollLock(showForm);
  const overlayCloseProps = useOverlayClickClose(() => setShowForm(false));
  if (!showForm) return null;

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : 16 }}
      {...overlayCloseProps}
    >
      <div style={{
        background: 'var(--bg-card)', borderRadius: isMobile ? '16px 16px 0 0' : 14,
        width: '100%', maxWidth: isMobile ? '100%' : 640,
        maxHeight: isMobile ? '92vh' : '90vh', overflowY: 'auto', overflowX: 'hidden',
        boxShadow: '0 -4px 40px rgba(0,0,0,0.15)',
        transform: isMobile ? `translateY(${sheetDragY}px)` : 'none',
        transition: sheetDragging.current ? 'none' : 'transform 0.32s cubic-bezier(0.34,1.56,0.64,1)',
        opacity: isMobile ? 1 - Math.min(sheetDragY / SHEET_DISMISS_THRESHOLD, 1) * 0.25 : 1,
      }}>

        {/* 모바일 드래그 핸들 영역 */}
        {isMobile && (
          <div ref={sheetHandleRef} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '12px 0 4px', cursor: 'grab', userSelect: 'none' }}>
            <div style={{
              width: sheetDragY > 30 ? 52 : 40, height: 5, borderRadius: 999,
              background: sheetDragY > 50 ? 'var(--text-muted)' : 'var(--border)',
              transition: 'width 0.15s, background 0.15s',
            }} />
            <div style={{
              fontSize: 10, fontWeight: 700, marginTop: 4, letterSpacing: '0.04em',
              color: sheetDragY > SHEET_DISMISS_THRESHOLD * 0.6 ? '#6366f1' : 'transparent',
              transition: 'color 0.15s',
            }}>놓으면 닫힘</div>
          </div>
        )}

        {/* 모달 헤더 — 검정 밴드 표준 (모바일은 위 드래그 핸들 아래라 라운드 0) */}
        <div style={stdModalHeaderBar(isMobile ? 0 : 14)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={stdModalHeaderIconBox}>{editId ? '✏️' : '📋'}</div>
            <span style={stdModalHeaderTitle}>{editId ? '견적서 수정' : '견적서 등록'}</span>
          </div>
          <button style={stdModalCloseBtn} onClick={() => setShowForm(false)}>✕</button>
        </div>

        <div style={{ padding: isMobile ? '8px 16px 32px' : 28 }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px 20px' }}>
            <div style={{ minWidth: 0 }}>
              <label style={labelStyle}>문서번호</label>
              <input style={{ ...inputStyle, background: 'var(--bg-subtle)', color: 'var(--text-muted)' }} value={form.quote_number} readOnly />
            </div>
            <div style={{ minWidth: 0 }}>
              <label style={labelStyle}>발행일</label>
              <input type="date" style={{ ...inputStyle, width: '100%', minWidth: 0, boxSizing: 'border-box', WebkitAppearance: 'none' }} value={form.issue_date} onChange={(e) => setField('issue_date', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>요청자 (마케팅담당자)</label>
              <select style={selectStyle} value={form.requester_name} onChange={(e) => setField('requester_name', e.target.value)}>
                <option value="">선택 안함</option>
                {salesPersons.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>공급받는 자 (상호) <span style={{ color: '#ef4444' }}>*</span></label>
              <input style={inputStyle} value={form.recipient_name} onChange={(e) => setField('recipient_name', e.target.value)} placeholder="상호명 입력" />
            </div>
            <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
              <label style={labelStyle}>사업자등록번호 <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>숫자만 입력</span></label>
              <input style={{ ...inputStyle, maxWidth: 220 }} value={form.business_number} onChange={(e) => setField('business_number', fmtBizNum(e.target.value))} placeholder="000-00-00000" maxLength={12} />
            </div>
            <div>
              <label style={labelStyle}>품목 <span style={{ color: '#ef4444' }}>*</span></label>
              <select style={selectStyle} value={form.product1_name} onChange={(e) => setField('product1_name', e.target.value)}>
                <option value="">선택</option>
                {PRODUCT_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>장수 <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>숫자만 입력</span></label>
              <input style={inputStyle} value={form.product1_qty} onChange={(e) => setField('product1_qty', fmtComma(e.target.value))} placeholder="0" />
            </div>
            <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
              <label style={labelStyle}>단가 <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>숫자만 입력</span></label>
              <input style={{ ...inputStyle, maxWidth: 220 }} value={form.product1_unit_price} onChange={(e) => setField('product1_unit_price', fmtComma(e.target.value))} placeholder="0" />
            </div>
            <div>
              <label style={labelStyle}>서비스품목</label>
              <select style={selectStyle} value={form.product2_name} onChange={(e) => setField('product2_name', e.target.value)}>
                <option value="">선택 안함</option>
                {PRODUCT_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>서비스장수 <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>숫자만 입력</span></label>
              <input style={inputStyle} value={form.product2_qty} onChange={(e) => setField('product2_qty', fmtComma(e.target.value))} placeholder="0" />
            </div>
            <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
              <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.vat_yn} onChange={(e) => setField('vat_yn', e.target.checked)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                부가세 10% 적용
              </label>
            </div>
            <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
              <label style={labelStyle}>무상제공품목</label>
              <FreeItemsEditor promotionalItems={promotionalItems} rows={freeRows} onChange={setFreeRows} />
            </div>
            <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
              <label style={labelStyle}>비고</label>
              <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 52, lineHeight: 1.5 }} value={form.memo} onChange={(e) => setField('memo', e.target.value)} placeholder="메모 (선택)" rows={2} />
            </div>
          </div>

          {formErr && (
            <div style={{ marginTop: 10, padding: '8px 12px', background: '#fef2f2', color: '#dc2626', borderRadius: 6, fontSize: 13 }}>{formErr}</div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 18, flexDirection: isMobile ? 'column' : 'row', justifyContent: 'flex-end' }}>
            {!isMobile && <button style={btnSecondary} onClick={() => setShowForm(false)}>취소</button>}
            <button style={{ ...btnPrimary, ...(isMobile ? { width: '100%', padding: '13px' } : {}) }} onClick={handleSave} disabled={saving}>
              {saving ? '저장 중...' : (editId ? '수정 저장' : '등록')}
            </button>
            {isMobile && <button style={{ ...btnSecondary, width: '100%', padding: '13px', textAlign: 'center' }} onClick={() => setShowForm(false)}>취소</button>}
          </div>
        </div>
      </div>
    </div>
  );
}
